import { useState, useEffect, useReducer, useContext, useCallback } from 'react';

import { getWhatsAppOptions } from '../../services/whatsappService';
import { AuthContext } from '../../context/Auth/AuthContext';

const reducer = (state, action) => {
  if (action.type === 'LOAD_WHATSAPPS') {
    const whatsApps = action.payload;

    return [...whatsApps];
  }

  if (action.type === 'UPDATE_WHATSAPPS') {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = whatsApp;
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }

  if (action.type === 'UPDATE_SESSION') {
    const session = action.payload;
    const id = session.whatsappId ?? session.id;
    const whatsAppIndex = state.findIndex((s) => s.id === id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = session.status;
      state[whatsAppIndex].updatedAt = session.updatedAt;
      state[whatsAppIndex].qrcode = session.qrcode ?? '';
      state[whatsAppIndex].retries = session.retries ?? 0;
      return [...state];
    } else {
      return [...state];
    }
  }

  if (action.type === 'DELETE_WHATSAPPS') {
    const whatsAppId = action.payload;

    const whatsAppIndex = state.findIndex((s) => s.id === whatsAppId);
    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const { user, socket } = useContext(AuthContext);

  // /whatsapp/options é operacional (apenas isAuth) — sem exigir connections:read,
  // que controla só o menu/gestão de conexões. Assim o contexto global (badges, lista
  // no atendimento) funciona para qualquer usuário sem 403.
  const fetchWhatsApps = useCallback(async () => {
    try {
      const { data } = await getWhatsAppOptions();
      dispatch({ type: 'LOAD_WHATSAPPS', payload: data });
      return data;
    } catch (err) {
      return null;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchSession = async () => {
      try {
        const { data } = await getWhatsAppOptions();
        dispatch({ type: 'LOAD_WHATSAPPS', payload: data });
        setLoading(false);
      } catch (_) {
        setLoading(false);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    if (!user.companyId) return undefined;

    const companyId = user.companyId;

    const onCompanyWhatsapp = (data) => {
      if (data.action === 'update') {
        dispatch({ type: 'UPDATE_WHATSAPPS', payload: data.whatsapp });
      }
      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_WHATSAPPS', payload: data.whatsappId });
      }
    };

    const onCompanyWhatsappSession = (data) => {
      if (data.action === 'update') {
        dispatch({ type: 'UPDATE_SESSION', payload: data.session });
      }
    };

    if (socket && typeof socket.on === 'function') {
      socket.on(`company-${companyId}-whatsapp`, onCompanyWhatsapp);
      socket.on(`company-${companyId}-whatsappSession`, onCompanyWhatsappSession);
      return () => {
        if (socket && typeof socket.off === 'function') {
          socket.off(`company-${companyId}-whatsapp`, onCompanyWhatsapp);
          socket.off(`company-${companyId}-whatsappSession`, onCompanyWhatsappSession);
        }
      };
    }

    return undefined;
  }, [socket, user.companyId]);

  const updateSession = (session) => {
    if (session) {
      dispatch({ type: 'UPDATE_SESSION', payload: session });
    }
  };

  return { whatsApps, loading, updateSession, fetchWhatsApps };
};

export default useWhatsApps;
